import React, { useEffect, useState } from "react";
import { Box, CssBaseline, Grid, Typography } from "@mui/material";
import { mainLayoutStyles, showNotification } from "../../../../../constants";

import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import DetailsPageHeader from "../../../../../common/DetailsPageHeader";
import { Toast } from "../../../../../common/alert";
import QTDetailsTextFieldWithLabelSection from "../../../../../common/QTDetailsTextFieldWithLabelSection";
import {
  DEFAULT_APP_VERSION_CONFIG,
  getAppVersionConfig,
  updateAppVersionConfig,
} from "../../hooks/AppVersionSettingsHook";

const trimValue = (value) => (value === null || value === undefined ? "" : String(value).trim());

// Firestore stores remindAfterHours as a double; blank/invalid input saves as null.
const toNumberOrNull = (value) => {
  const trimmed = trimValue(value);
  if (trimmed === "") return null;
  const parsed = parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const AppVersionSettingsContainer = () => {
  const [editEnabled, setEditEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState({ ...DEFAULT_APP_VERSION_CONFIG });

  useEffect(() => {
    handleGetConfig();
  }, []);

  const handleGetConfig = async () => {
    try {
      const data = await getAppVersionConfig();
      setConfig(data);
    } catch (error) {
      console.log("error getting appVersion config:", error.message);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = {
        mobile: {
          latestVersion: trimValue(config.mobile?.latestVersion),
          message: trimValue(config.mobile?.message),
          androidUrl: trimValue(config.mobile?.androidUrl),
          iosUrl: trimValue(config.mobile?.iosUrl),
          remindAfterHours: toNumberOrNull(config.mobile?.remindAfterHours),
        },
        webApp: {
          latestVersion: trimValue(config.webApp?.latestVersion),
        },
        admin: {
          latestVersion: trimValue(config.admin?.latestVersion),
        },
      };
      await updateAppVersionConfig(payload);
      await handleGetConfig();
      showNotification("App Version settings updated successfully.");
      setEditEnabled(false);
    } catch (error) {
      console.log("error updating appVersion config:", error.message);
      Toast("error", "error updating App Version settings");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (value) => {
    setEditEnabled(value);
    // Revert unsaved edits when leaving edit mode.
    if (!value) handleGetConfig();
  };

  // Immutable nested state setter: section = mobile | webApp | admin.
  const setField = (section, key, value) =>
    setConfig({ ...config, [section]: { ...config[section], [key]: value } });

  const textField = (label, section, key, extraProps = {}) => (
    <QTDetailsTextFieldWithLabelSection
      label={label}
      value={config[section]?.[key] ?? ""}
      disabled={true}
      editEnabled={editEnabled}
      noTextTransform={true}
      onChange={(e) => setField(section, key, e.target.value)}
      {...extraProps}
    />
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={"App Version Settings"}
        bgColor="#fff"
        buttonText={"Edit"}
        editEnabled={editEnabled}
        setEditEnabled={handleCancel}
        cancelButton={editEnabled ? true : false}
        handleSave={() => handleSave()}
        loading={loading}
      />
      <GeneralAdminSidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"}>
          <Grid container style={dataContainerStyles} direction={"column"}>
            {/* Mobile */}
            <Typography style={sectionTitleStyles}>Mobile</Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {textField("Latest Version", "mobile", "latestVersion")}
              </Grid>
              <Grid item container direction={"column"} xs>
                {textField("Remind After Hours", "mobile", "remindAfterHours", {
                  type: "number",
                })}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {textField("Android URL", "mobile", "androidUrl")}
              </Grid>
              <Grid item container direction={"column"} xs>
                {textField("iOS URL", "mobile", "iosUrl")}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {textField("Message", "mobile", "message", {
                  multiline: true,
                  rows: 3,
                })}
              </Grid>
              <Grid item container direction={"column"} xs />
            </Grid>

            {/* WebApp */}
            <Typography style={sectionTitleStyles}>WebApp</Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {textField("Latest Version", "webApp", "latestVersion")}
              </Grid>
              <Grid item container direction={"column"} xs />
            </Grid>

            {/* Admin */}
            <Typography style={sectionTitleStyles}>Admin</Typography>
            <Grid container>
              <Grid item container direction={"column"} xs>
                {textField("Latest Version", "admin", "latestVersion")}
              </Grid>
              <Grid item container direction={"column"} xs />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AppVersionSettingsContainer;

const dataContainerStyles = {
  borderRadius: 12,
  boxShadow: " 0px 0px 30px 0px rgba(80, 85, 136, 0.15)",
  background: "var(--gray-99, #FCFCFC)",
  padding: "40px 30px",
};

const sectionTitleStyles = {
  color: "#020303",
  fontSize: 18,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
  marginTop: 8,
  marginBottom: 20,
};
